import JSZip from "jszip";

const APPENDED_CHAPTER_HREF_PREFIX = "fic-reader-appended-chapter";

const posixDirname = (path = "") => {
  const normalized = String(path || "").replace(/\\/g, "/");
  const lastSlash = normalized.lastIndexOf("/");
  return lastSlash === -1 ? "" : normalized.slice(0, lastSlash);
};

const joinEpubPath = (...parts) =>
  parts
    .filter(Boolean)
    .join("/")
    .replace(/\\/g, "/")
    .replace(/\/{2,}/g, "/");

const relativeEpubPath = (fromPath = "", toPath = "") => {
  const fromDir = posixDirname(fromPath);
  const fromParts = fromDir ? fromDir.split("/").filter(Boolean) : [];
  const toParts = String(toPath || "")
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean);

  while (fromParts.length && toParts.length && fromParts[0] === toParts[0]) {
    fromParts.shift();
    toParts.shift();
  }

  return [...fromParts.map(() => ".."), ...toParts].join("/") || ".";
};

const stripUnsafeXmlChars = (value = "") =>
  Array.from(String(value)).filter((char) => {
    const code = char.charCodeAt(0);
    return code === 0x09 || code === 0x0a || code === 0x0d || (code >= 0x20 && code !== 0x7f);
  }).join("");

const escapeXmlText = (value = "") =>
  stripUnsafeXmlChars(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const buildAppendedChapterLabel = (order) => `Added Chapter ${order}`;

const convertPlainTextToAppendedHtml = (content = "") => {
  const normalized = String(content || "").replace(/\r\n?/g, "\n");
  const paragraphs = normalized.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter(Boolean);
  if (!paragraphs.length) {
    return `<p>${escapeXmlText(normalized).replace(/\n/g, "<br />")}</p>`;
  }
  return paragraphs
    .map((paragraph) => `<p>${escapeXmlText(paragraph).replace(/\n/g, "<br />")}</p>`)
    .join("\n");
};

const buildAppendedChapterMarkup = ({ title, content, contentHtml }) => `<?xml version="1.0" encoding="utf-8"?>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>${escapeXmlText(title)}</title>
    <meta charset="utf-8" />
    <style>
      body { margin: 0; padding: 0 0 2rem; }
      h1 { margin: 0 0 1.25rem; }
      .fic-reader-appended-chapter__body { white-space: normal; }
      .fic-reader-appended-chapter__body p,
      .fic-reader-appended-chapter__body blockquote,
      .fic-reader-appended-chapter__body li { white-space: pre-wrap; }
      .fic-reader-appended-chapter__body p,
      .fic-reader-appended-chapter__body blockquote,
      .fic-reader-appended-chapter__body ul,
      .fic-reader-appended-chapter__body ol { margin: 0 0 1em; }
    </style>
  </head>
  <body>
    <h1>${escapeXmlText(title)}</h1>
    <div class="fic-reader-appended-chapter__body">${contentHtml || convertPlainTextToAppendedHtml(content)}</div>
  </body>
</html>`;

const buildAppendedChapterHref = (order) => `${APPENDED_CHAPTER_HREF_PREFIX}-${order}.xhtml`;

export const isZipBuffer = (buffer) => {
  if (!buffer) return false;
  const bytes = new Uint8Array(buffer);
  return bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07) &&
    (bytes[3] === 0x04 || bytes[3] === 0x06 || bytes[3] === 0x08);
};

export const buildDerivedEpubWithAppendedChapters = async (sourceBuffer, chapters = []) => {
  if (!sourceBuffer || !Array.isArray(chapters) || chapters.length === 0) {
    return sourceBuffer;
  }

  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const zip = await JSZip.loadAsync(sourceBuffer);
  const containerXml = await zip.file("META-INF/container.xml")?.async("string");
  if (!containerXml) throw new Error("Missing EPUB container");

  const containerDoc = parser.parseFromString(containerXml, "application/xml");
  const rootfile = containerDoc.querySelector("rootfile");
  const opfPath = rootfile?.getAttribute("full-path") || "";
  if (!opfPath) throw new Error("Missing EPUB package path");

  const opfXml = await zip.file(opfPath)?.async("string");
  if (!opfXml) throw new Error("Missing EPUB package");

  const opfDoc = parser.parseFromString(opfXml, "application/xml");
  const packageDir = posixDirname(opfPath);
  const manifest = opfDoc.querySelector("manifest");
  const spine = opfDoc.querySelector("spine");
  if (!manifest || !spine) throw new Error("Missing EPUB manifest or spine");

  chapters.forEach((chapter, index) => {
    const order = index + 1;
    const chapterId = `${APPENDED_CHAPTER_HREF_PREFIX}-${order}`;
    const chapterHref = buildAppendedChapterHref(order);
    const chapterPath = joinEpubPath(packageDir, chapterHref);

    if (!manifest.querySelector(`item[id="${chapterId}"]`)) {
      const manifestItem = opfDoc.createElement("item");
      manifestItem.setAttribute("id", chapterId);
      manifestItem.setAttribute("href", chapterHref);
      manifestItem.setAttribute("media-type", "application/xhtml+xml");
      manifest.appendChild(manifestItem);
    }

    if (!spine.querySelector(`itemref[idref="${chapterId}"]`)) {
      const itemRef = opfDoc.createElement("itemref");
      itemRef.setAttribute("idref", chapterId);
      spine.appendChild(itemRef);
    }

    zip.file(chapterPath, buildAppendedChapterMarkup({
      title: chapter.title || buildAppendedChapterLabel(order),
      content: chapter.content_text || chapter.content || "",
      contentHtml: chapter.content_html || chapter.contentHtml || ""
    }));
  });

  const navItem = opfDoc.querySelector('manifest item[properties~="nav"]') || null;
  if (navItem) {
    const navPath = joinEpubPath(packageDir, navItem.getAttribute("href") || "");
    const navXml = await zip.file(navPath)?.async("string");
    if (navXml) {
      const navDoc = parser.parseFromString(navXml, "application/xhtml+xml");
      const tocNav =
        navDoc.querySelector('nav[*|type="toc"]') ||
        navDoc.querySelector('nav[epub\\:type="toc"]') ||
        navDoc.querySelector("nav");
      if (tocNav) {
        let tocList = tocNav.querySelector("ol, ul");
        if (!tocList) {
          tocList = navDoc.createElement("ol");
          tocNav.appendChild(tocList);
        }
        chapters.forEach((chapter, index) => {
          const order = index + 1;
          const href = relativeEpubPath(navPath, joinEpubPath(packageDir, buildAppendedChapterHref(order)));
          if (tocList.querySelector(`a[href="${href}"]`)) return;
          const li = navDoc.createElement("li");
          const link = navDoc.createElement("a");
          link.setAttribute("href", href);
          link.textContent = chapter.title || buildAppendedChapterLabel(order);
          li.appendChild(link);
          tocList.appendChild(li);
        });
        zip.file(navPath, serializer.serializeToString(navDoc));
      }
    }
  }

  const ncxId = spine.getAttribute("toc");
  if (ncxId) {
    const ncxManifestItem = manifest.querySelector(`item[id="${ncxId}"]`);
    const ncxHref = ncxManifestItem?.getAttribute("href") || "";
    const ncxPath = joinEpubPath(packageDir, ncxHref);
    const ncxXml = ncxHref ? await zip.file(ncxPath)?.async("string") : "";
    if (ncxXml) {
      const ncxDoc = parser.parseFromString(ncxXml, "application/xml");
      const navMap = ncxDoc.querySelector("navMap");
      if (navMap) {
        let nextPlayOrder = Array.from(ncxDoc.querySelectorAll("navPoint")).reduce(
          (max, navPoint) => Math.max(max, Number(navPoint.getAttribute("playOrder")) || 0),
          0
        );
        chapters.forEach((chapter, index) => {
          const order = index + 1;
          const href = relativeEpubPath(ncxPath, joinEpubPath(packageDir, buildAppendedChapterHref(order)));
          if (navMap.querySelector(`content[src="${href}"]`)) return;
          nextPlayOrder += 1;
          const navPoint = ncxDoc.createElement("navPoint");
          navPoint.setAttribute("id", `${APPENDED_CHAPTER_HREF_PREFIX}-nav-${order}`);
          navPoint.setAttribute("playOrder", String(nextPlayOrder));
          const navLabel = ncxDoc.createElement("navLabel");
          const textNode = ncxDoc.createElement("text");
          textNode.textContent = chapter.title || buildAppendedChapterLabel(order);
          navLabel.appendChild(textNode);
          const contentNode = ncxDoc.createElement("content");
          contentNode.setAttribute("src", href);
          navPoint.appendChild(navLabel);
          navPoint.appendChild(contentNode);
          navMap.appendChild(navPoint);
        });
        zip.file(ncxPath, serializer.serializeToString(ncxDoc));
      }
    }
  }

  zip.file(opfPath, serializer.serializeToString(opfDoc));
  return zip.generateAsync({ type: "arraybuffer" });
};

export const fetchAppendedChapters = async ({ apiUrl, epubId, rememberToken = "" }) => {
  const response = await fetch(`${apiUrl}/get-appended-chapters.php?epub_id=${epubId}`, {
    credentials: "include",
    cache: "no-store",
    headers: rememberToken ? { "X-Remember-Token": rememberToken } : {}
  });
  const data = await response.json();
  return data?.success && Array.isArray(data.chapters) ? data.chapters : [];
};

export const buildOfflineEpubBlob = async ({ apiUrl, epubId, rememberToken = "" }) => {
  const response = await fetch(`${apiUrl}/serve-epub.php?id=${epubId}&include_appended=0`, {
    credentials: "include",
    cache: "no-store",
    headers: rememberToken ? { "X-Remember-Token": rememberToken } : {}
  });
  if (!response.ok) {
    throw new Error(`EPUB request failed with ${response.status}`);
  }

  const baseBlob = await response.blob();
  const appendedChapters = await fetchAppendedChapters({
    apiUrl,
    epubId,
    rememberToken
  });

  if (!appendedChapters.length) {
    return baseBlob;
  }

  const baseBuffer = await baseBlob.arrayBuffer();
  const mergedBuffer = await buildDerivedEpubWithAppendedChapters(
    baseBuffer,
    appendedChapters
  );
  return new Blob([mergedBuffer], { type: "application/epub+zip" });
};

export const fetchOfflineReadyBookMetadata = async ({
  apiUrl,
  epubId,
  userId,
  rememberToken = "",
  fallback = null
}) => {
  try {
    const response = await fetch(`${apiUrl}/get-epub.php?id=${epubId}&uid=${userId}`, {
      credentials: "include",
      cache: "no-store",
      headers: rememberToken ? { "X-Remember-Token": rememberToken } : {}
    });
    const data = await response.json();
    if (data?.success && data.epub) {
      return data.epub;
    }
  } catch (error) {}

  return fallback;
};
